import type { EngineMovementEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type EngineMovementRow = EngineMovementEvent & { pageId?: string };

/**
 * Dense grid for `engineMovementEvents` (engine install/removal job-card rows, DR-3249).
 * One row per (engine, side) — an exchange card shows two, for two different engines.
 * Used on the entity view (with a source-page column) and the page view (pass no
 * `pageIdToPosition`).
 */
export function EngineMovementTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: EngineMovementRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: EngineMovementRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>ESN</Th>
          <Th>Role</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId ?? 'page'}-${r.esn}-${r.role}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="font-mono">{r.esn || '—'}</Td>
            <Td>{formatRole(r.role)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatRole(role: EngineMovementEvent['role']): string {
  if (role === 'on') return 'Installed (ON)';
  if (role === 'off') return 'Removed (OFF)';
  return '—';
}
